import { Trophy } from 'lucide-react';
import React from 'react';

import { Badge, Divider, HStack, Text, VStack } from '@eluve/components';

type DateStatus = 'active' | 'not_started' | 'expired';

const dateStatusConfig: Record<
  DateStatus,
  { label: string; variant: 'success' | 'info' | 'secondary' }
> = {
  active: { label: 'Active', variant: 'success' },
  not_started: { label: 'Not Started', variant: 'info' },
  expired: { label: 'Expired', variant: 'secondary' },
};

export type ExperimentMatch = {
  experimentId: string;
  experimentName: string;
  posthogFlagKey: string;
  variantId: string;
  variantName: string;
  posthogVariantKey: string;
  variantConfig: Record<string, unknown>;
  rank: number;
  isWinner: boolean;
  isInjected: boolean;
  dateStatus: DateStatus;
};

interface ExperimentMatchCardProps {
  match: ExperimentMatch;
}

export const ExperimentMatchCard: React.FC<ExperimentMatchCardProps> = ({
  match,
}) => {
  const cardClasses = match.isWinner
    ? 'border-border-positive-subtle bg-bg-positive-subtle'
    : 'border-border-primary bg-bg-gray-subtle';

  return (
    <div className={`w-full rounded-lg border p-3 ${cardClasses}`}>
      <VStack gap={2}>
        <HStack gap={2} align="center" className="flex-wrap">
          {match.isWinner && <Trophy className="size-4 text-text-positive" />}
          <Text weight="semibold">{match.experimentName}</Text>
          <Badge variant={match.isWinner ? 'success' : 'secondary'}>
            Rank: {match.rank}
          </Badge>
          <Badge variant={dateStatusConfig[match.dateStatus].variant}>
            {dateStatusConfig[match.dateStatus].label}
          </Badge>
          {match.isInjected && <Badge variant="info">Injected</Badge>}
          {match.isWinner && <Badge variant="success">Winner</Badge>}
        </HStack>

        <Divider />

        <div className="grid grid-cols-3 gap-2">
          <VStack gap={0}>
            <Text size="sm" color="tertiary">
              Variant
            </Text>
            <Text size="sm">{match.variantName}</Text>
          </VStack>
          <VStack gap={0}>
            <Text size="sm" color="tertiary">
              PostHog Key
            </Text>
            <Text size="sm" className="font-mono">
              {match.posthogVariantKey}
            </Text>
          </VStack>
          <VStack gap={0}>
            <Text size="sm" color="tertiary">
              Flag Key
            </Text>
            <Text size="sm" className="font-mono">
              {match.posthogFlagKey}
            </Text>
          </VStack>
        </div>

        {Object.keys(match.variantConfig).length > 0 && (
          <VStack gap={1}>
            <Text size="sm" color="tertiary">
              Parsed Config
            </Text>
            <pre className="overflow-x-auto rounded bg-bg-gray-subtle p-2 text-xs">
              {JSON.stringify(match.variantConfig, null, 2)}
            </pre>
          </VStack>
        )}
      </VStack>
    </div>
  );
};
